import axios from "axios";
import { compressFiles } from "@/lib/imageCompressor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  // CRITICAL: send the session cookie on every request. Without this,
  // every authenticated GET (/report, /usage, /budget, /family/members,
  // /referrals/me, /privacy/export, /early-adopter/status, etc.) returns
  // 401 and the user sees "Request failed with status code 401" toasts
  // immediately after sign-in.
  withCredentials: true,
});

// ── 401 race retry (2026-06-26) ────────────────────────────────────────
// Right after Google OAuth redirect the session cookie is written by the
// server via Set-Cookie but the browser occasionally hasn't attached it to
// the very first authenticated fetch (e.g. Dashboard's initial /report or
// /orders/by-month call). Instead of exploding into a red toast, retry the
// same request once after a short delay. If the second attempt still 401s,
// we let the failure propagate — the user genuinely isn't signed in.
const RETRY_ON_401_HEADER = "X-Cartiq-401-Retried";
const RETRY_ON_401_DELAY_MS = 300;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    if (
      status === 401 &&
      cfg &&
      !cfg.headers?.[RETRY_ON_401_HEADER] &&
      // Never retry the auth endpoints themselves — they're the ones telling
      // us we're not signed in. Only retry actual data fetches.
      !/\/auth\/(session|me|logout|delete-account)$/.test(cfg.url || "")
    ) {
      await new Promise((r) => setTimeout(r, RETRY_ON_401_DELAY_MS));
      cfg.headers = { ...(cfg.headers || {}), [RETRY_ON_401_HEADER]: "1" };
      return api.request(cfg);
    }
    return Promise.reject(error);
  },
);

export const fetchReport = async (reportId) => {
  const res = await api.get(`/report/${reportId}`);
  return res.data;
};

export const uploadPdfs = async ({ files, phone, budget }) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  if (phone) fd.append("phone", phone);
  if (budget) fd.append("budget", String(budget));
  const res = await axios.post(`${API}/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    // Endpoint returns almost immediately now (async job). Keep a generous
    // client timeout as a safety net for slow networks.
    timeout: 90000,
    withCredentials: true,
  });
  return res.data;
};

/**
 * Upload many files reliably by chunking them into smaller multipart batches.
 *
 * The Kubernetes ingress in front of our preview/production fronts the
 * backend with a request-body cap that 502s on payloads above ~2 MB. A user
 * dropping 15+ image screenshots at once easily blows past that limit. This
 * helper splits the file list into batches sized to fit comfortably under the
 * cap, awaits each batch's report (so cumulative aggregation is deterministic),
 * and returns the LAST batch's response — which the backend builds as a
 * cumulative report covering ALL prior uploads in the user's history.
 *
 * @param {{
 *   files: File[],
 *   phone?: string|null,
 *   budget?: number,
 *   batchSize?: number,
 *   onProgress?: ({batch, totalBatches, fileCount, status}) => void,
 *   pollIntervalMs?: number,
 *   pollTimeoutMs?: number,
 * }} opts
 */
export const uploadPdfsBatched = async ({
  files,
  phone,
  budget,
  batchSize = 4,
  onProgress,
  onCompressionSummary,
  pollIntervalMs = 2500,
  pollTimeoutMs = 5 * 60 * 1000,
}) => {
  if (!files?.length) throw new Error("No files");
  // Compress images client-side first. Phone screenshots are typically 1-3 MB
  // each; canvas resize + JPEG q0.78 brings them to ~50-100 KB without hurting
  // Claude Vision OCR accuracy. PDFs and already-small files pass through.
  onProgress?.({ batch: 0, totalBatches: 0, fileCount: files.length, status: "compressing" });
  const prepared = await compressFiles(files, onCompressionSummary);
  const batches = [];
  for (let i = 0; i < prepared.length; i += batchSize) {
    batches.push(prepared.slice(i, i + batchSize));
  }
  let last = null;
  for (let i = 0; i < batches.length; i += 1) {
    onProgress?.({
      batch: i + 1,
      totalBatches: batches.length,
      fileCount: batches[i].length,
      status: "uploading",
    });
    const res = await uploadPdfs({ files: batches[i], phone, budget });
    if (!res?.reportId) {
      throw new Error("Upload accepted but no reportId returned.");
    }
    onProgress?.({
      batch: i + 1,
      totalBatches: batches.length,
      fileCount: batches[i].length,
      status: "processing",
    });
    // Wait for THIS batch's report to be ready before firing the next, so
    // the cumulative aggregation each batch reads from grocery_history is
    // deterministic and the final report covers everything.
    const ready = await pollReportUntilReady(res.reportId, {
      intervalMs: pollIntervalMs,
      timeoutMs: pollTimeoutMs,
    });
    last = { ...res, reportData: ready.reportData ?? ready, ready };
    onProgress?.({
      batch: i + 1,
      totalBatches: batches.length,
      fileCount: batches[i].length,
      status: "ready",
    });
  }
  return last;
};

/**
 * Poll /api/report/:id until status is "ready" or "failed".
 * Calls onProgress({status, pollCount}) on each poll so callers can update UI.
 * @param {string} reportId
 * @param {{intervalMs?: number, timeoutMs?: number, onProgress?: function}} opts
 */
export const pollReportUntilReady = async (reportId, opts = {}) => {
  const interval = opts.intervalMs ?? 2500;
  const deadline = Date.now() + (opts.timeoutMs ?? 5 * 60 * 1000); // 5 min cap
  let pollCount = 0;
  while (Date.now() < deadline) {
    const data = await fetchReport(reportId);
    pollCount += 1;
    if (opts.onProgress) {
      try {
        opts.onProgress({ status: data?.status, pollCount });
      } catch {
        /* swallow */
      }
    }
    if (data?.status === "ready") return data;
    if (data?.status === "failed") {
      const err = new Error(data?.error || "Report generation failed");
      err.status = "failed";
      throw err;
    }
    await new Promise((r) => setTimeout(r, interval));
  }
  const err = new Error("Report generation timed out. Please retry.");
  err.status = "timeout";
  throw err;
};

export const updateBudget = async ({ userId, budget }) => {
  const res = await api.post(`/budget`, { userId, budget });
  return res.data;
};

export const connectWhatsapp = async ({ phone, userId }) => {
  const res = await api.post(`/whatsapp/connect`, { phone, userId });
  return res.data;
};

export const confirmOrder = async ({ userId, reportId, items, totalAmount }) => {
  const res = await api.post(`/orders/confirm`, { userId, reportId, items, totalAmount });
  return res.data;
};

export const fetchUsage = async (phone) => {
  const res = await api.get(`/usage`, { params: phone ? { phone } : {} });
  return res.data;
};

export const joinIntelligenceWaitlist = async ({ name, email, source = "intelligence_tab" }) => {
  const res = await api.post(`/intelligence/waitlist`, { name, email, source });
  return res.data;
};

export const fetchAskUsage = async () => {
  const res = await api.get(`/ask/usage`);
  return res.data;
};

export const askCartIQ = async ({ question, conversation_history = [], session_id = null }) => {
  const res = await api.post(`/ask`, {
    question,
    conversation_history,
    session_id,
  });
  return res.data;
};

// P1-19 · Weekly proactive macro-shift welcome for the CartIQ Ask drawer.
// Returns {show, message, card}. Backend debounces to once per user per 7d
// so calling this on every drawer open is safe.
export const fetchMacroShiftWelcome = async () => {
  const res = await api.get(`/ask/welcome/macro-shift`);
  return res.data;
};


